/**
 * terrain_features.ts — PURE finders for the debug menu's "teleport to a
 * terrain feature" (2026-09-10): the nearest (or a random) cave mouth,
 * lake shore, river bank, waterfall lip, sea shore, open sea, mountain peak,
 * forest stand, settlement plateau rim, terrace strata, and each of the six
 * biomes — computed from the SAME oracles the world is built from
 * (`caveSites`, the baked hydrology, `terrainHeight`, `forestDensity`,
 * `biomeAt`, `settlementSites`), so a spot is always a real feature of the
 * Commons and never a table to keep in sync. Harness-pinned
 * (`verify:terrain-features`); the menu only teleports to what this returns.
 */
import { terrainHeight, terrainHeightBase, biomeAt, terraceHeight, BIOMES, PLATEAU_RADIUS, type Biome } from '@shared/terrain.js';
import { settlementSites } from '@shared/settlements.js';
import { caveSites } from '@shared/caves.js';
import { CAVE_PORTAL_FRONT } from './cave_gen.js';
/** The cave teleport stands this far in front of the mouth — outside the portal outcrop. */
export const CAVE_SPOT_BACK = Math.ceil(CAVE_PORTAL_FRONT) + 3;
import { forestDensity } from './forest_field.js';
import { RIVER_STRIDE, RIVER_FLAG_FALL } from './hydrology_bake.js';
import type { BakedLake } from './lakes.js';

export type FeatureKind =
  | 'cave' | 'lake' | 'river' | 'waterfall' | 'shore' | 'sea' | 'mountain' | 'forest' | 'plateau' | 'terrace'
  | Biome;

export const FEATURE_KINDS: ReadonlyArray<{ kind: FeatureKind; label: string; hint: string }> = [
  { kind: 'cave', label: 'Cave', hint: 'a cave mouth — walk in, the tunnel runs uphill' },
  { kind: 'lake', label: 'Lake', hint: 'the shore of a baked lake' },
  { kind: 'river', label: 'River', hint: 'a river bank' },
  { kind: 'waterfall', label: 'Waterfall', hint: 'the bank beside a waterfall lip' },
  { kind: 'shore', label: 'Ocean shore', hint: 'the beach where the land meets the sea' },
  { kind: 'sea', label: 'Open sea', hint: '200 m offshore — you will swim' },
  { kind: 'mountain', label: 'Mountain', hint: 'the highest peak within 40 km (random: elsewhere on the continent)' },
  { kind: 'forest', label: 'Forest', hint: 'a dense stand of the woods' },
  { kind: 'plateau', label: 'Plateau rim', hint: 'the edge of a settlement plateau, looking at the town' },
  { kind: 'terrace', label: 'Terraces', hint: 'the strata band where the highlands step' },
  { kind: 'regolith_plains', label: 'Regolith plains', hint: 'biome' },
  { kind: 'crystal_forest', label: 'Crystal forest', hint: 'biome' },
  { kind: 'dune_desert', label: 'Dune desert', hint: 'biome' },
  { kind: 'rocky_highlands', label: 'Rocky highlands', hint: 'biome' },
  { kind: 'moss_wetland', label: 'Moss wetland', hint: 'biome' },
  { kind: 'fungal_basin', label: 'Fungal basin', hint: 'biome' },
];

export interface FeatureData {
  seed: string;
  /** Decoded rivers (flat RIVER_STRIDE arrays: x, z, water, width, depth, flag) — null when no bake is loaded. */
  rivers: ReadonlyArray<ArrayLike<number>> | null;
  lakes: readonly BakedLake[];
  sea: number;
}

export interface FeatureSpot {
  kind: FeatureKind;
  x: number;
  z: number;
  y: number;
  /** Human line for the debug note ("Cave #12 · 3.4 km"). */
  label: string;
  distM: number;
}

export type FeatureMode = 'nearest' | 'random';

/** The continent's half-width (m) — random origins stay on the map. */
export const CONTINENT_HALF_M = 1_000_000;
/** How far a scan walks out from its origin before giving up (m). */
export const SCAN_MAX_M = 90_000;
const SCAN_STEP_M = 250;

function fmtDist(d: number): string { return d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`; }

/** Outward ring scan: nearest sample satisfying `pred`, rings widening ~8 %
 *  per step so the whole 90 km costs a few thousand samples. */
export function ringScan(fromX: number, fromZ: number, pred: (x: number, z: number) => boolean, maxR = SCAN_MAX_M, step0 = SCAN_STEP_M): { x: number; z: number; spacing: number } | null {
  if (pred(fromX, fromZ)) return { x: fromX, z: fromZ, spacing: step0 };
  for (let r = step0; r <= maxR; r += Math.max(step0, r * 0.08)) {
    const spacing = Math.max(step0, r * 0.08);
    const n = Math.max(8, Math.ceil((2 * Math.PI * r) / spacing));
    const a0 = (r / step0) * 0.61803 * Math.PI * 2; // rotate each ring so samples never align
    for (let i = 0; i < n; i++) {
      const a = a0 + (i / n) * Math.PI * 2;
      const x = fromX + Math.cos(a) * r, z = fromZ + Math.sin(a) * r;
      if (pred(x, z)) return { x, z, spacing };
    }
  }
  return null;
}

/** Continent-wide COARSE pre-pass (an 8 km lattice, ~62k cheap samples):
 *  the nearest lattice cell satisfying `pred`, for kinds a local ring scan
 *  may not reach (a biome two hundred kilometres off). Refine with a local
 *  ring scan from the cell afterwards. */
export function coarseNearest(fromX: number, fromZ: number, pred: (x: number, z: number) => boolean, cell = 8000): { x: number; z: number } | null {
  let best: { x: number; z: number } | null = null, bestD = Infinity;
  const ox = Math.round(fromX / cell) * cell, oz = Math.round(fromZ / cell) * cell;
  for (let z = -CONTINENT_HALF_M; z <= CONTINENT_HALF_M; z += cell) {
    for (let x = -CONTINENT_HALF_M; x <= CONTINENT_HALF_M; x += cell) {
      const dd = (x - ox) * (x - ox) + (z - oz) * (z - oz);
      if (dd >= bestD) continue;
      if (pred(x, z)) { bestD = dd; best = { x, z }; }
    }
  }
  return best;
}

/** A local ring scan, then the coarse continental pass + a ring scan from
 *  its cell — nearest-first either way. */
function scanNearest(fromX: number, fromZ: number, pred: (x: number, z: number) => boolean, maxR: number, step0: number): { x: number; z: number } | null {
  const local = ringScan(fromX, fromZ, pred, maxR, step0);
  if (local) return local;
  const cell = coarseNearest(fromX, fromZ, pred);
  if (!cell) return null;
  return ringScan(cell.x, cell.z, pred, 16_000, step0) ?? cell;
}

function spot(kind: FeatureKind, seed: string, x: number, z: number, label: string, fromX: number, fromZ: number): FeatureSpot {
  return { kind, x, z, y: terrainHeight(seed, x, z), label, distM: Math.hypot(x - fromX, z - fromZ) };
}

function randomOrigin(rand: () => number): { x: number; z: number } {
  return { x: (rand() * 2 - 1) * CONTINENT_HALF_M, z: (rand() * 2 - 1) * CONTINENT_HALF_M };
}

/** The nearest river point (or a random one); `fall` restricts to waterfall lips. */
function riverPoint(d: FeatureData, fromX: number, fromZ: number, mode: FeatureMode, rand: () => number, fall: boolean): { r: ArrayLike<number>; k: number } | null {
  if (!d.rivers || d.rivers.length === 0) return null;
  if (mode === 'random') {
    const cands: Array<{ r: ArrayLike<number>; k: number }> = [];
    for (const r of d.rivers) {
      const m = r.length / RIVER_STRIDE;
      if (fall) { for (let k = 0; k < m; k++) if ((r[k * RIVER_STRIDE + 5] & RIVER_FLAG_FALL) !== 0) cands.push({ r, k }); }
      else cands.push({ r, k: Math.floor(rand() * m) % m });
    }
    return cands.length ? cands[Math.floor(rand() * cands.length) % cands.length] : null;
  }
  let best: { r: ArrayLike<number>; k: number } | null = null, bestD = Infinity;
  for (const r of d.rivers) {
    const m = r.length / RIVER_STRIDE;
    for (let k = 0; k < m; k++) {
      const o = k * RIVER_STRIDE;
      if (fall && (r[o + 5] & RIVER_FLAG_FALL) === 0) continue;
      const dx = r[o] - fromX, dz = r[o + 1] - fromZ;
      const dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = { r, k }; }
    }
  }
  return best;
}

/** The bank beside a river point: perpendicular to the local flow, half a
 *  width plus a stride out, on whichever side stands above the water. */
function bankOf(seed: string, r: ArrayLike<number>, k: number): { x: number; z: number } {
  const m = r.length / RIVER_STRIDE;
  const o = k * RIVER_STRIDE;
  const on = (k + 1 < m ? k + 1 : k - 1) * RIVER_STRIDE;
  const px = r[o], pz = r[o + 1], water = r[o + 2], width = r[o + 3];
  let dx = r[on] - px, dz = r[on + 1] - pz;
  const len = Math.hypot(dx, dz) || 1;
  dx /= len; dz /= len;
  const out = width / 2 + 5;
  const a = { x: px - dz * out, z: pz + dx * out };
  const b = { x: px + dz * out, z: pz - dx * out };
  const ha = terrainHeight(seed, a.x, a.z), hb = terrainHeight(seed, b.x, b.z);
  if (ha > water + 0.3) return a;
  if (hb > water + 0.3) return b;
  return ha >= hb ? a : b;
}

/** Local peak search — a SUMMIT, not a lattice max: a coarse lattice ranks
 *  candidates, each climbs the ground by steepest ascent (60 m → 5 m
 *  stencils) to its own top, and the best summit must then DOMINATE its
 *  6 km neighbourhood at a 300 m sampling — any higher sample climbs in
 *  turn, until nothing around stands taller (rugged noise hides ridges
 *  from any single lattice; the harness caught one 2 km off). */
function peakNear(seed: string, cx: number, cz: number, halfM: number): { x: number; z: number; h: number } {
  type P = { x: number; z: number; h: number };
  const at = (x: number, z: number): P => ({ x, z, h: terrainHeightBase(seed, x, z) });
  const climb = (p0: P): P => {
    let p = p0;
    for (const st of [240, 60, 15, 5]) {
      for (let it = 0; it < 80; it++) {
        let best = p;
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          const q = at(p.x + Math.cos(a) * st, p.z + Math.sin(a) * st);
          if (q.h > best.h) best = q;
        }
        if (best === p) break;
        p = best;
      }
    }
    return p;
  };
  const coarse: P[] = [];
  const step = Math.max(200, halfM / 50); // 800 m over ±40 km — 10k samples
  for (let z = cz - halfM; z <= cz + halfM; z += step) {
    for (let x = cx - halfM; x <= cx + halfM; x += step) coarse.push(at(x, z));
  }
  coarse.sort((a, b) => b.h - a.h);
  let best: P = { x: cx, z: cz, h: -Infinity };
  for (const c of coarse.slice(0, 12)) {
    const r = climb(c);
    if (r.h > best.h) best = r;
  }
  // Dominance: the summit must be the highest sample of its 6 km square.
  for (let round = 0; round < 40; round++) { // each round climbs to a strictly taller summit — 40 is far past convergence
    let taller: P | null = null;
    for (let z = -3000; z <= 3000; z += 300) {
      for (let x = -3000; x <= 3000; x += 300) {
        const q = at(best.x + x, best.z + z);
        if (q.h > best.h + 0.5 && (!taller || q.h > taller.h)) taller = q;
      }
    }
    if (!taller) break;
    const r = climb(taller);
    if (r.h <= best.h) break;
    best = r;
  }
  return { x: best.x, z: best.z, h: terrainHeight(seed, best.x, best.z) };
}

export function findFeature(d: FeatureData, kind: FeatureKind, fromX: number, fromZ: number, mode: FeatureMode = 'nearest', rand: () => number = Math.random): FeatureSpot | null {
  const seed = d.seed;
  const land = (x: number, z: number): boolean => terrainHeight(seed, x, z) > d.sea + 2;
  switch (kind) {
    case 'cave': {
      const sites = caveSites(seed);
      if (sites.length === 0) return null;
      const s = mode === 'random'
        ? sites[Math.floor(rand() * sites.length) % sites.length]
        : sites.reduce((b, c) => (Math.hypot(c.x - fromX, c.z - fromZ) < Math.hypot(b.x - fromX, b.z - fromZ) ? c : b));
      // Five metres OUT of the mouth on the apron, facing the hill.
      // In front of the portal OUTCROP (its rim can reach ~7 m before the mouth), not inside it.
      return spot('cave', seed, s.x - s.dx * CAVE_SPOT_BACK, s.z - s.dz * CAVE_SPOT_BACK, `Cave #${s.id} (mouth at ${Math.round(s.h)} m)`, fromX, fromZ);
    }
    case 'lake': {
      if (d.lakes.length === 0) return null;
      const l = mode === 'random'
        ? d.lakes[Math.floor(rand() * d.lakes.length) % d.lakes.length]
        : d.lakes.reduce((b, c) => (Math.hypot(c.cx - fromX, c.cz - fromZ) < Math.hypot(b.cx - fromX, b.cz - fromZ) ? c : b));
      // Walk from the centroid toward the nearest bbox edge until the ground
      // rises above the held water: that is the shore; stand 6 m up it.
      const toX = Math.abs(l.x1 - l.cx) < Math.abs(l.cx - l.x0) ? 1 : -1;
      const reach = Math.max(l.x1 - l.x0, 60) + 200;
      let sx = l.cx + toX * reach, sz = l.cz;
      for (let t = 6; t <= reach; t += 6) {
        const x = l.cx + toX * t;
        if (terrainHeight(seed, x, l.cz) > l.level + 0.6) { sx = x + toX * 6; sz = l.cz; break; }
      }
      return spot('lake', seed, sx, sz, `Lake at ${Math.round(l.level)} m (${l.depth.toFixed(1)} m deep)`, fromX, fromZ);
    }
    case 'river':
    case 'waterfall': {
      const p = riverPoint(d, fromX, fromZ, mode, rand, kind === 'waterfall');
      if (!p) return null;
      const bank = bankOf(seed, p.r, p.k);
      const o = p.k * RIVER_STRIDE;
      const label = kind === 'waterfall'
        ? `Waterfall lip (water ${Math.round(p.r[o + 2])} m)`
        : `River bank (${p.r[o + 3].toFixed(0)} m wide, water ${Math.round(p.r[o + 2])} m)`;
      return spot(kind, seed, bank.x, bank.z, label, fromX, fromZ);
    }
    case 'shore':
    case 'sea': {
      const origin = mode === 'random' ? randomOrigin(rand) : { x: fromX, z: fromZ };
      const wet = (x: number, z: number): boolean => terrainHeight(seed, x, z) < d.sea - 1.5;
      const water = ringScan(origin.x, origin.z, wet, SCAN_MAX_M * 3, 400);
      if (!water) return null;
      // Walk back toward the origin — up to a full ring spacing, the sample
      // before this one was dry — in coarse steps to the first dry sample,
      // then refine to the waterline in 2 m steps; stand 4 m up the beach.
      let dx = origin.x - water.x, dz = origin.z - water.z;
      const len = Math.hypot(dx, dz) || 1;
      dx /= len; dz /= len;
      let bx = water.x, bz = water.z;
      let dryT = -1;
      for (let t = 25; t <= water.spacing * 1.5 + 25; t += 25) {
        if (!wet(water.x + dx * t, water.z + dz * t)) { dryT = t; break; }
      }
      if (dryT > 0) {
        let t = dryT;
        for (let u = dryT - 25; u <= dryT; u += 2) { if (!wet(water.x + dx * u, water.z + dz * u)) { t = u; break; } }
        // Past the waterline (sea − 1.5 → sea + 0.5) to dry sand.
        while (t < dryT + 400 && terrainHeight(seed, water.x + dx * t, water.z + dz * t) < d.sea + 0.5) t += 2;
        bx = water.x + dx * (t + 4); bz = water.z + dz * (t + 4);
      }
      if (kind === 'shore') return spot('shore', seed, bx, bz, 'Ocean shore', fromX, fromZ);
      // Open sea: 200 m past the shoreline, on the water side.
      let ox = bx - dx * 200, oz = bz - dz * 200;
      if (terrainHeight(seed, ox, oz) > d.sea - 1.5) { ox = water.x; oz = water.z; }
      return spot('sea', seed, ox, oz, `Open sea (${(d.sea - terrainHeight(seed, ox, oz)).toFixed(0)} m deep)`, fromX, fromZ);
    }
    case 'mountain': {
      const origin = mode === 'random' ? randomOrigin(rand) : { x: fromX, z: fromZ };
      const pk = peakNear(seed, origin.x, origin.z, 40_000);
      return spot('mountain', seed, pk.x, pk.z, `Peak at ${Math.round(pk.h)} m`, fromX, fromZ);
    }
    case 'forest': {
      const origin = mode === 'random' ? randomOrigin(rand) : { x: fromX, z: fromZ };
      const f = scanNearest(origin.x, origin.z, (x, z) => land(x, z) && forestDensity(seed, x, z, d.sea) >= 0.7, SCAN_MAX_M, SCAN_STEP_M);
      if (!f) return null;
      return spot('forest', seed, f.x, f.z, `Forest (density ${forestDensity(seed, f.x, f.z, d.sea).toFixed(2)})`, fromX, fromZ);
    }
    case 'plateau': {
      const sites = settlementSites(seed);
      if (sites.length === 0) return null;
      const s = mode === 'random'
        ? sites[Math.floor(rand() * sites.length) % sites.length]
        : sites.reduce((b, c) => (Math.hypot(c.centerX - fromX, c.centerZ - fromZ) < Math.hypot(b.centerX - fromX, b.centerZ - fromZ) ? c : b));
      // On the rim, on the side facing the player (a random bearing otherwise).
      let ax = fromX - s.centerX, az = fromZ - s.centerZ;
      if (mode === 'random' || Math.hypot(ax, az) < 1) { const a = rand() * Math.PI * 2; ax = Math.cos(a); az = Math.sin(a); }
      const len = Math.hypot(ax, az) || 1;
      const r = PLATEAU_RADIUS[s.tier] + 25;
      return spot('plateau', seed, s.centerX + (ax / len) * r, s.centerZ + (az / len) * r, `Plateau rim of ${s.tier} site ${s.id}`, fromX, fromZ);
    }
    case 'terrace': {
      const origin = mode === 'random' ? randomOrigin(rand) : { x: fromX, z: fromZ };
      const t = scanNearest(origin.x, origin.z, (x, z) => {
        const base = terrainHeightBase(seed, x, z);
        return base > d.sea + 2 && Math.abs(terraceHeight(seed, x, z, base) - base) > 1.5;
      }, SCAN_MAX_M, 400);
      if (!t) return null;
      return spot('terrace', seed, t.x, t.z, `Terrace strata at ${Math.round(terrainHeight(seed, t.x, t.z))} m`, fromX, fromZ);
    }
    default: {
      if (!(BIOMES as readonly string[]).includes(kind)) return null;
      const origin = mode === 'random' ? randomOrigin(rand) : { x: fromX, z: fromZ };
      // The biome test first (cheap, and a coarse continental pass may run
      // it 60k times); the land test only on a match.
      const b = scanNearest(origin.x, origin.z, (x, z) => biomeAt(seed, x, z) === kind && land(x, z), SCAN_MAX_M, 350);
      if (!b) return null;
      return spot(kind, seed, b.x, b.z, `${kind.replace(/_/g, ' ')} biome`, fromX, fromZ);
    }
  }
}

export function featureLine(s: FeatureSpot): string {
  return `${s.label} · ${fmtDist(s.distM)} away · (${Math.round(s.x)}, ${Math.round(s.z)}) at ${Math.round(s.y)} m`;
}
